import * as Select from '@radix-ui/react-select'
import clsx from 'clsx'
import { CheckIcon, ChevronDownIcon } from 'lucide-react'
import React, { useState } from 'react'

const SelectItem = React.forwardRef<
  HTMLDivElement,
  {
    value: string;
    outputValue: string;
    currentValue: string;
  }
>(({ value, outputValue, currentValue }, forwardedRef) => {
  const [isFocused, setIsFocused] = useState(false)

  const isCurrentlySelected = value === currentValue
  return (
    <Select.Item
      className={clsx(
        'p-2 px-4 text-gray-500 flex items-center relative focus:outline-none cursor-pointer align-center content-center',
        { 'text-thatch-purple-secondary': isCurrentlySelected },
        { 'bg-thatch-purple-secondary text-white': isFocused },
        { 'bg-white': !isFocused }
      )}
      value={value}
      ref={forwardedRef}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      <Select.ItemText>
        {outputValue}
      </Select.ItemText>
      <Select.ItemIndicator className='pl-2'>
        <CheckIcon />
      </Select.ItemIndicator>
    </Select.Item>
  )
})
SelectItem.displayName = 'SelectItem'

export const BillingYearDropdown = ({
  yearsAvailable,
  selectedYear,
  handleSetYear,
  isLoading = false,
}: {
  yearsAvailable: number[];
  selectedYear: number;
  handleSetYear: (value: string) => void;
  isLoading?: boolean;
}) => {
  if (isLoading) {
    return (
      <div className='flex items-center shrink-0'>
        <div className='text-normal text-ds-text-secondary'>
          Loading...
        </div>
      </div>
    )
  }

  if (yearsAvailable.length < 2) {
    return (
      <div className='flex items-center shrink-0'>
        <div className='text-normal text-ds-text-secondary'>
          {selectedYear}
        </div>
      </div>
    )
  }

  return (
    <div className='flex items-center shrink-0'>
      <Select.Root
        value={selectedYear.toString()}
        onValueChange={handleSetYear}
      >
        <Select.Trigger
          className='text-gray-500 inline-flex items-center justify-center rounded h-[2.25rem] px-4 bg-white focus:outline-none cursor-pointer shadow-[0_1px_4px_rgba(0,0,0,0.1)] ring-1 ring-black ring-opacity-5 md:rounded-lg text-sm'
          aria-label='Year Options'
        >
          <Select.Value placeholder='Select a year...'>
            {selectedYear}
          </Select.Value>

          <Select.Icon className='pl-2'>
            <ChevronDownIcon />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position='popper'
            sideOffset={5}
            className='overflow-hidden rounded-md border shadow-[0_1px_4px_rgba(0,0,0,0.1)]'
          >
            <Select.ScrollUpButton />

            <Select.Viewport>
              {yearsAvailable.map((year) => (
                <SelectItem
                  key={year}
                  value={year.toString()}
                  outputValue={year.toString()}
                  currentValue={selectedYear.toString()}
                />
              ))}
            </Select.Viewport>

            <Select.ScrollDownButton />
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  )
}
